import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';
import {
  barcodeKeys,
  createProductWithVariants,
  normalizeBarcode,
  uploadAndAttachProductImage,
} from '@dukanti/shared';
import type {
  Product,
  ProductImageWithUrl,
  CreateProductInput,
  CreateProductWithVariantsInput,
} from '@dukanti/shared';
import { supabase } from '@/lib/supabase';
import { productKeys } from './use-products';

export interface CreateProductMutationInput {
  input?: CreateProductInput;
  product?: CreateProductWithVariantsInput['product'];
  mode?: CreateProductWithVariantsInput['mode'];
  options?: CreateProductWithVariantsInput['options'];
  variants?: CreateProductWithVariantsInput['variants'];
  /** Ordered list of already-validated File objects (up to 5). Index
   *  position defines initial `order_index` in product_images. */
  files?: File[];
}

export interface CreateProductResult {
  product: Product;
  images: ProductImageWithUrl[];
  /** True when the product row committed but >=1 image upload failed.
   *  Mirrors useCreateStore's `logoFailed` flag. The list still contains
   *  every image that DID succeed; the rest are reported via
   *  `failedFileNames` so the UI can name them in the warning toast. */
  imagesFailed: boolean;
  failedFileNames: string[];
}

/**
 * Create a draft product (AC10) and upload up to 5 images.
 *
 * D3 transactional per-image flow: each image is uploaded + attached
 * in its own try/catch. A single failure is collected into
 * `failedFileNames` and the remaining files keep uploading — a
 * partial-failure outcome is explicit in the result rather than
 * silently swallowed.
 *
 * §18 item 8: on success we AWAIT the list refetch (not just
 * invalidate) so the `/products` landing after navigation is
 * guaranteed to include the new row.
 */
export function useCreateProduct(): UseMutationResult<
  CreateProductResult,
  unknown,
  CreateProductMutationInput
> {
  const queryClient = useQueryClient();

  return useMutation<CreateProductResult, unknown, CreateProductMutationInput>({
    mutationFn: async ({
      input,
      product: productInput,
      mode = 'simple',
      options = [],
      variants,
      files = [],
    }) => {
      const effectiveProduct = input
        ? {
            store_id: input.store_id,
            name: input.name,
            description: input.description,
            currency: input.currency,
            category_id: input.category_id,
          }
        : productInput;
      if (!effectiveProduct) {
        throw Object.assign(new Error('missing product payload'), { code: '22023' });
      }
      // Step 1: create the product row. RLS CHECK fires here.
      const product = await createProductWithVariants(supabase, {
        product: effectiveProduct,
        mode,
        options,
        variants: variants ?? [
          {
            price_cents: input?.price_cents ?? 0,
            sale_price_cents: input?.sale_price_cents ?? null,
            currency: input?.currency ?? 'ILS',
            inventory: input?.inventory ?? 0,
            active: true,
            is_default: true,
            display_order: 0,
            option_value_ids: [],
          },
        ],
      });

      // Step 2: per-file transactional upload+attach. Partial failure
      // is tolerated; every file that can upload will.
      const images: ProductImageWithUrl[] = [];
      const failedFileNames: string[] = [];
      let imagesFailed = false;

      for (let i = 0; i < files.length; i++) {
        const file = files[i]!;
        try {
          const image = await uploadAndAttachProductImage(supabase, {
            storeId: effectiveProduct.store_id,
            productId: product.id,
            orderIndex: i,
            file,
          });
          images.push(image);
        } catch (err) {
          imagesFailed = true;
          failedFileNames.push(file.name);
          console.error('[useCreateProduct] image upload failed', file.name, err);
          // continue — other files might succeed
        }
      }

      return { product, images, imagesFailed, failedFileNames };
    },
    onSuccess: async (_data, variables) => {
      const storeId = variables.input?.store_id ?? variables.product?.store_id;
      if (!storeId) return;
      // Guarantee the list reflects the new product before the user
      // sees /products (§18 item 8).
      await queryClient.refetchQueries({
        queryKey: productKeys.list(storeId),
      });
      await queryClient.invalidateQueries({ queryKey: ['stock-summary', storeId] });
      await queryClient.invalidateQueries({ queryKey: ['billing', 'entitlement', storeId] });
      await Promise.all(
        (variables.variants ?? [])
          .map((variant) => normalizeBarcode(variant.barcode_value ?? ''))
          .filter((barcode) => barcode.length > 0)
          .map((barcode) =>
            queryClient.invalidateQueries({
              queryKey: barcodeKeys.lookup(storeId, barcode),
            }),
          ),
      );
    },
  });
}
